import { LoadingStage, ErrorReason } from '../selectors/getSpListViewState';
import { PerformanceDatapoint, DatapointStatus } from 'owa-analytics';
import { FileListLoadingStatus } from '../Schema/FileListLoadingStatus';
import { getGroupMailboxExternalId } from 'owa-groups-shared-store';

let perfDatapoint: PerformanceDatapoint = null;
let lastGroup: string = null;

// undefined indicates not finished
let isRecentsSuccess: boolean = undefined;
let isSpSuccess: boolean = undefined;

export const LogRecentsCompleted = (
    cacheListStatus: FileListLoadingStatus,
    refreshListStatus: FileListLoadingStatus,
    cacheListError: string,
    refreshListError: string
): void => {
    if (perfDatapoint != null) {
        perfDatapoint.addCheckmark('Recents completed');

        /**
         * In logging the recent file loading component of the lists load datapoint we want to consider
         * both the initial cache call and the refresh call. Both of these calls are represented here.
         *
         * Both datapoints have several success and error conditions. The cache/refreshListStatus is derived
         * from the success of the actual call itself as well as internal status reporting from a
         * successful call (given by ResponseStatusType) which can be success/partialSuccess/failure
         *
         * We also report RecentCacheError and RecentRefreshError which is either the result of an error with the call itself
         * or is part of a successful call which is given by the return results as FailureReason.
         */
        if (cacheListStatus === FileListLoadingStatus.Failed) {
            isRecentsSuccess = false;
            perfDatapoint.addCustomData({ RecentsCacheStatus: 'error' });
        }

        if (refreshListStatus === FileListLoadingStatus.Failed) {
            isRecentsSuccess = false;
            perfDatapoint.addCustomData({ RecentsRefreshStatus: 'error' });
        }

        // Success checks come after error checks because one success for either call results in overall success
        if (cacheListStatus === FileListLoadingStatus.Success) {
            isRecentsSuccess = true;
            perfDatapoint.addCustomData({ RecentsCacheStatus: 'success' });
        } else if (cacheListStatus === FileListLoadingStatus.PartialSuccess) {
            isRecentsSuccess = true;
            perfDatapoint.addCustomData({ RecentsCacheStatus: 'partialSuccess' });
        }

        if (refreshListStatus === FileListLoadingStatus.Success) {
            isRecentsSuccess = true;
            perfDatapoint.addCustomData({ RecentsRefreshStatus: 'success' });
        } else if (refreshListStatus === FileListLoadingStatus.PartialSuccess) {
            isRecentsSuccess = true;
            perfDatapoint.addCustomData({ RecentsRefreshStatus: 'partialSuccess' });
        }

        // Report errors regardless of status in case of failureReason in success scenario
        perfDatapoint.addCustomData({ RecentsRefreshError: refreshListError });
        perfDatapoint.addCustomData({ RecentsCacheError: cacheListError });

        EndPerfDataPointWhenFilesHubLoaded();
    }
};

export const LogSpAbsent = (): void => {
    if (perfDatapoint != null) {
        perfDatapoint.addCheckmark('No SP site');
        isSpSuccess = true;
        perfDatapoint.addCustomData({ SPStatus: 'NoSPSite' });
        EndPerfDataPointWhenFilesHubLoaded();
    }
};

export const LogSpFilesSuccess = (): void => {
    if (perfDatapoint != null) {
        perfDatapoint.addCheckmark('SP completed');
        isSpSuccess = true;
        perfDatapoint.addCustomData({ SPStatus: 'success' });
        EndPerfDataPointWhenFilesHubLoaded();
    }
};

export const LogSpFilesTokenTransientFailure = (): void => {
    if (perfDatapoint != null) {
        perfDatapoint.addCheckmark('SP completed');

        isSpSuccess = false;
        perfDatapoint.addCustomData({ SPError: 'TokenTransientError' });
        perfDatapoint.addCustomData({ SPStatus: 'UnsureError' });

        EndPerfDataPointWhenFilesHubLoaded();
    }
};

export const LogGroupDetailsComplete = (smtpAddress): void => {
    if (
        perfDatapoint != null &&
        (!perfDatapoint.propertyBag ||
            (perfDatapoint.propertyBag && !perfDatapoint.propertyBag['groupExternalId']))
    ) {
        perfDatapoint.addCustomProperty('groupExternalId', getGroupMailboxExternalId(smtpAddress));
    }
};

export const LogSpFilesFailure = (errorReason: ErrorReason): void => {
    if (perfDatapoint != null) {
        perfDatapoint.addCheckmark('SP completed');

        isSpSuccess = false;
        if (typeof errorReason === 'number' || typeof errorReason === 'string') {
            perfDatapoint.addCustomData({ SPError: ErrorReason[errorReason] });
        }

        perfDatapoint.addCustomData({ SPStatus: 'error' });

        EndPerfDataPointWhenFilesHubLoaded();
    }
};

export const LogSpLoadingStatus = (loadingStage: LoadingStage): void => {
    if (perfDatapoint != null) {
        perfDatapoint.addCheckmark(LoadingStage[loadingStage]);
    }
};

export const EndFilesHubLoadListDataPointWhenNotComplete = (reason: string): void => {
    if (perfDatapoint != null) {
        let error: Error;
        if (reason != undefined && reason != null) {
            error = new Error(reason);
        }

        perfDatapoint.endWithError(DatapointStatus.RequestNotComplete, error);
        perfDatapoint = null;
        lastGroup = null;
        isRecentsSuccess = undefined;
        isSpSuccess = undefined;
    }
};

export const StartFilesHubListLoadDatapoint = (smtpAddress: string): void => {
    if (perfDatapoint == null) {
        perfDatapoint = createDatapoint(smtpAddress);
        lastGroup = smtpAddress;
    } else if (lastGroup != null && lastGroup.toLowerCase() != smtpAddress.toLowerCase()) {
        perfDatapoint.endWithError(DatapointStatus.RequestNotComplete, new Error('group changed'));

        // start new dataPoint as the previous one was for a different group
        perfDatapoint = createDatapoint(smtpAddress);
        lastGroup = smtpAddress;
        isRecentsSuccess = undefined;
        isSpSuccess = undefined;
    }
};

function createDatapoint(smtpAddress: string) {
    const datapoint = new PerformanceDatapoint('GroupFilesHubListsLoad', { isQos: true });

    return datapoint;
}

const EndPerfDataPointWhenFilesHubLoaded = (): void => {
    // only if both SP and recents are completed
    if (isSpSuccess != undefined && isRecentsSuccess != undefined) {
        if (isSpSuccess == true && isRecentsSuccess == true) {
            perfDatapoint.end();
        } else {
            perfDatapoint.endWithError(DatapointStatus.ClientError);
        }
    }
};
